<template>
    <div class="card shadow mb-3 bg-white rounded balise-info-card">
        <div class="card-body">

            <!-- Display -->
            <div v-if="!updatingData">
                <div class="balise-header">
                    <div class="balise-header-main">
                        <div class="balise-vehicle-block">
                            <VehicleNameDisplay :balise="baliseInfo" :showStatuses="true" :linkToMap="true" />
                        </div>

                        <div class="balise-position-block">
                            <div class="position-top">
                                <div class="position-date-block">
                                    <div class="position-date">
                                        {{ baliseInfo.displayLastSeen }}
                                    </div>

                                    <div class="position-ago">
                                        {{ $filters.timeAgo(baliseInfo.lastSeen) }}
                                    </div>
                                </div>
                            </div>

                            <div class="position-address">
                                {{ baliseInfo.lastAddress || 'Adresse non renseignée' }}
                            </div>

                            <div class="position-coordinates">
                                <LatLongDisplay :latitude="baliseInfo.lastLatitude"
                                    :longitude="baliseInfo.lastLongitude" />
                            </div>
                        </div>
                    </div>
                </div>

                <hr class="section-separator" />

                <div class="section-line-title mb-3">
                    <span>Balise</span>

                    <button v-if="!isOperator && !isViewer" class="balise-edit-button" type="button"
                        v-on:click="startUpdating()">
                        <ButtonDisplay :type="'edit'" />
                    </button>
                </div>

                <div class="info-grid">

                    <div class="info-line">
                        <span class="info-label">Modèle</span>
                        <span class="info-value">
                            {{ baliseTypesDisplay[baliseInfo.type] || baliseInfo.type || 'Non renseigné' }}
                        </span>
                    </div>

                    <div class="info-line">
                        <span class="info-label">IMEI</span>
                        <span class="info-value">
                            <IMEIDisplay :imei="baliseInfo.imei" />
                        </span>
                    </div>

                    <div v-if="baliseInfo.createdAt != null" class="info-line">
                        <span class="info-label">Mise en service</span>
                        <span class="info-value">
                            {{ createdAtDisplay }}
                        </span>
                    </div>

                    <div v-if="baliseInfo.lastTemperature != null" class="info-line">
                        <span class="info-label">Temp. système</span>
                        <span class="info-value">
                            <TemperatureDisplay :temperature="baliseInfo.lastTemperature" />
                        </span>
                    </div>

                    <div class="info-line">
                        <span class="info-label">Tag</span>
                        <span class="info-value">
                            {{ yesNoDisplay(baliseInfo.hasTag) }}
                        </span>
                    </div>

                    <div class="info-line">
                        <span class="info-label">+ après contact</span>
                        <span class="info-value">
                            {{ yesNoDisplay(baliseInfo.plusAContact) }}
                        </span>
                    </div>

                    <div v-if="baliseInfo.displayLastTagPresentAt" class="info-line">
                        <span class="info-label">Dernier badge présent</span>
                        <span class="info-value">
                            {{ baliseInfo.displayLastTagPresentAt }}
                        </span>
                    </div>

                    <div v-if="baliseInfo.displayLastTagAbsentAt" class="info-line">
                        <span class="info-label">Dernier badge absent</span>
                        <span class="info-value">
                            {{ baliseInfo.displayLastTagAbsentAt }}
                        </span>
                    </div>
                </div>

                <hr class="section-separator" />

                <div class="section-line-title mb-3">
                    <span>Configuration</span>
                </div>

                <div class="info-grid">
                    <div class="info-line">
                        <span class="info-label">Télésurveillance</span>

                        <span class="info-value">
                            {{ baliseInfo.remoteMonitoring == 1 ? 'Active' : 'Désactivée' }}
                        </span>
                    </div>

                    <div class="info-line">
                        <span class="info-label">Offre commerciale</span>

                        <span class="info-value">
                            {{
                                baliseInfo.commercialPackageId == null
                                    ? 'Non renseignée'
                                    : baliseInfo.commercialPackage.name
                            }}
                        </span>
                    </div>

                    <div v-if="baliseInfo.free == 1" class="info-line">
                        <span class="info-label">Facturation</span>

                        <span class="info-value">
                            Balise non facturable
                        </span>
                    </div>
                </div>
            </div>

            <!-- Update -->
            <div v-else class="container">
                <div class="row d-flex justify-content-between my-2">
                    <div class="col-12">
                        <div class="input-group">
                            <span class="input-group-text" id="input-name">Nom</span>

                            <input class="form-control" type="text" v-model="updatedName" aria-describedby="input-name"
                                required>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-between my-2">
                    <div class="col-12">
                        <div class="input-group">
                            <label class="input-group-text" for="inputGroupSelect01">
                                Groupe
                            </label>

                            <select class="form-select" id="inputGroupSelect01" v-model="updatedGroup" required>
                                <option :value="null">
                                    Aucun groupe
                                </option>

                                <option v-for="group in groups" :key="group" :value="group[1].id">
                                    {{ group[0] }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-center text-center mb-3">
                    <div class="col-12">
                        <div class="input-group">
                            <span class="input-group-text" id="input-immat">
                                Immatriculation
                            </span>

                            <input class="form-control" type="text" v-model="updatedImmat"
                                aria-describedby="input-immat" required>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-center text-center mb-3">
                    <div class="col-12">
                        <div class="input-group">
                            <label class="input-group-text" for="inputGroupSelectType">
                                Type
                            </label>

                            <select class="form-select" id="inputGroupSelectType" v-model="updatedType" required>
                                <option :value="null">Aucun type</option>
                                <option :value="'VOITURE'">Voiture</option>
                                <option :value="'SCOOTER'">Scooter</option>
                                <option :value="'MOTO'">Moto</option>
                                <option :value="'CAMION'">Camion</option>
                                <option :value="'CAMIONETTE'">Camionette</option>
                                <option :value="'REMORQUE'">Remorque</option>
                                <option :value="'AUTRE'">Autre</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-center text-center mb-3">
                    <div class="col-12">
                        <div class="input-group">
                            <span class="input-group-text" id="input-make">
                                Marque
                            </span>

                            <input class="form-control" type="text" v-model="updatedMake" aria-describedby="input-make"
                                maxlength="30" required>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-center text-center mb-3">
                    <div class="col-12">
                        <div class="input-group">
                            <span class="input-group-text" id="input-model">
                                Modèle
                            </span>

                            <input class="form-control" type="text" v-model="updatedModel"
                                aria-describedby="input-model" maxlength="30" required>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-center text-center mb-3">
                    <div class="col-12">
                        <div class="input-group">
                            <span class="input-group-text" id="input-color">
                                Couleur
                            </span>

                            <input class="form-control" type="text" v-model="updatedColor"
                                aria-describedby="input-color" maxlength="30" required>
                        </div>
                    </div>
                </div>

                <div class="row d-flex justify-content-center text-center mb-3">
                    <div class="col-12">
                        <p>Selectionnez l'icône :</p>

                        <img v-if="baliseInfo" class="border rounded pointer-cursor"
                            :src="`/img/cars/${baliseInfo.icon}`" :alt="baliseInfo.icon"
                            v-on:click="$emit('changeBalise', baliseInfo.id); $emit('setBaliseToUpdate', baliseInfo.id)"
                            data-bs-toggle="modal" data-bs-target="#iconPicker">
                    </div>
                </div>

                <hr class="my-4" />

                <div class="d-flex justify-content-center">
                    <button class="btn btn-primary m-1"
                        v-on:click="updateData(updatedName, updatedImmat, updatedGroup, updatedType, updatedMake, updatedModel, updatedColor); updatingData = false">
                        Mettre à jour
                    </button>

                    <button class="btn btn-light m-1" v-on:click="updatingData = false">
                        Annuler
                    </button>
                </div>
            </div>
            <!-- End Update -->

        </div>
    </div>
</template>

<script>
import ButtonDisplay from '@/components/display/ButtonDisplay.vue'
import VehicleNameDisplay from '@/components/display/VehicleNameDisplay.vue'
import LatLongDisplay from '@/components/display/LatLongDisplay.vue'
import IMEIDisplay from '@/components/display/IMEIDisplay.vue'
import TemperatureDisplay from '@/components/display/TemperatureDisplay.vue'
import BaliseService from '@/services/balise.service'
import UserHelper from '@/helpers/user.helper.js'

export default {
    props: ['baliseInfo', 'groups'],
    components: {
        VehicleNameDisplay,
        LatLongDisplay,
        TemperatureDisplay,
        IMEIDisplay,
        ButtonDisplay,
    },
    data() {
        return {
            updatingData: false,
            lock: true,
            baliseTypesDisplay: {
                LL301: "GEONEO",
                TLW2: "GEOTRACK W2",
                PIONEERX100: "GEOTRACK P100",
                FMB920: "GEOTRACK B92",
                FMC920: "GEOTRACK C92",
                FMC130: "GEOTRACK C13",
                KNIGHTX100: "GEOTRACK X100",
                FMC800: "GEOBD 2",
                TWIGONE: "TWIG ONE",
                TT10: "TT10",
                TLW212B: "GEO TRAILER",
                NS10: "NS10",
                NS20: "NS20",
                GH5200: "GH5200"
            }
        };
    },
    computed: {
        createdAtDisplay() {
            if (!this.baliseInfo?.createdAt) return null;

            const raw = String(this.baliseInfo.createdAt);
            const date = new Date(raw);

            if (!Number.isNaN(date.getTime())) {
                return date.toLocaleDateString('fr-FR');
            }

            return raw.slice(0, 10).split('-').reverse().join('/');
        },
        isViewer() {
            return UserHelper.isViewer(this.$store.state.auth.user);
        },
        isClientAdmin() {
            return UserHelper.isClientAdmin(this.$store.state.auth.user);
        },
        isAdmin() {
            return UserHelper.isAdmin(this.$store.state.auth.user);
        },
        isOperator() {
            return UserHelper.isOperator(this.$store.state.auth.user);
        }
    },
    watch: {
        baliseInfo(newBI) {
            if (this.updatingData) return
            this.updatedName = newBI.name
            this.updatedImmat = newBI.immat
            this.updatedGroup = newBI.groupId
            this.updatedType = newBI.vehicle
            this.updatedMake = newBI.make
            this.updatedModel = newBI.model
            this.updatedColor = newBI.color
        }
    },
    methods: {
        yesNoDisplay(value) {
            if (value === true || value === 1) return 'Oui';
            if (value === false || value === 0) return 'Non';
            return 'Non renseigné';
        },
        startUpdating() {
            this.updatedName = this.baliseInfo.name
            this.updatedImmat = this.baliseInfo.immat
            this.updatedGroup = this.baliseInfo.groupId
            this.updatedType = this.baliseInfo.vehicle
            this.updatedMake = this.baliseInfo.make
            this.updatedModel = this.baliseInfo.model
            this.updatedColor = this.baliseInfo.color
            this.updatingData = true
        },
        updateData(name, immat, groupId, type, make, model, color) {
            BaliseService.updateData(this.baliseInfo.id, {
                name,
                registrationPlate: immat,
                groupId,
                vehicle: type,
                make,
                model,
                color
            })
                .then(() => {
                    this.$emit("refresh")
                })
                .catch(e => {
                    console.log(e)
                })
        }
    }
}
</script>

<style scoped>
.balise-info-card {
    position: relative;
}

.balise-info-card .card-body {
    padding: 1rem;
}

.balise-header-main {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(220px, 0.85fr);
    gap: 1rem;
    align-items: stretch;
}

.balise-vehicle-block {
    min-width: 0;
    width: 100%;
    max-width: 100%;
}

.balise-vehicle-block :deep(.vehicle-card) {
    width: 100%;
    justify-content: flex-start !important;
}

.balise-position-block {
    min-width: 0;
    padding-left: 0.75rem;
    padding-top: 0.35rem;
    border-left: 1px solid rgba(15, 23, 42, 0.12);
}

.position-top {
    display: flex;
    align-items: flex-start;
}

.position-date-block {
    min-width: 0;
    text-align: right;
}

.position-date {
    font-size: 0.78rem;
    line-height: 1.15;
    color: #6b7280;
    white-space: normal;
}

.position-ago {
    margin-top: 0.15rem;
    font-size: 0.76rem;
    line-height: 1.15;
    color: #6b7280;
    white-space: nowrap;
}

.position-address {
    margin-top: 1rem;
    font-size: 0.86rem;
    line-height: 1.3;
    color: #4b5563;
    overflow-wrap: anywhere;
}

.position-coordinates {
    margin-top: 0.35rem;
    font-size: 0.78rem;
    line-height: 1.2;
}

.position-coordinates :deep(*) {
    font-size: 0.78rem;
    line-height: 1.2;
}

.balise-edit-button {
    padding: 0;
    border: none;
    background: transparent;
}

.section-separator {
    margin: 0.85rem 0;
    border-color: rgba(15, 23, 42, 0.1);
    opacity: 1;
}

.section-line-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    font-weight: 700;
    color: #111827;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.45rem 1.5rem;
}

.info-line {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
    min-width: 0;
    padding: 0.15rem 0;
}

.info-label {
    font-size: 0.86rem;
    color: #111827;
    white-space: nowrap;
}

.info-value {
    min-width: 0;
    font-size: 0.86rem;
    color: #6b7280;
    text-align: right;
    overflow-wrap: anywhere;
}

@media (max-width: 1199.98px) {
    .balise-header-main {
        grid-template-columns: 1fr;
    }

    .balise-vehicle-block {
        width: 100%;
    }

    .balise-position-block {
        padding-left: 0;
        padding-top: 0.75rem;
        border-left: none;
        border-top: 1px solid rgba(15, 23, 42, 0.12);
    }
}

@media (max-width: 767.98px) {
    .balise-info-card .card-body {
        padding: 0.85rem;
    }

    .info-grid {
        grid-template-columns: 1fr;
        gap: 0.35rem;
    }

    .section-line-title {
        flex-direction: row;
        align-items: center;
    }

    .position-top {
        flex-direction: column;
        gap: 0.25rem;
    }

    .position-date-block {
        text-align: left;
    }
}

@media (max-width: 420px) {
    .info-line {
        flex-direction: column;
        gap: 0.1rem;
    }

    .info-value {
        text-align: left;
    }
}
</style>